// Google Analytics (GA4) helper.
//
// The gtag.js script + config call live in public/index.html — that handles
// automatic pageview tracking. This helper exists for explicit custom events
// (signup completed, book order initiated, purchase, etc.).
//
// Usage:
//   import { trackEvent } from '../utils/analytics';
//   trackEvent('book_started', { book_type: 'PERSONAL' });
//
// Failure mode: if window.gtag is not loaded (ad-blocker, offline, GA outage),
// the call is silently skipped. Analytics never breaks the user flow.

const GA_MEASUREMENT_ID = 'G-HQMRNBECX4';

// GA event name -> Meta (Facebook/Instagram) Pixel standard event. Only the conversion
// events that matter for ad optimization are mirrored; mapping them here means the call
// sites stay untouched (one trackEvent call feeds both GA and the Pixel).
const META_EVENTS = {
  sign_up: 'CompleteRegistration',
  begin_checkout: 'InitiateCheckout',
  purchase: 'Purchase',
};

export function trackEvent(name, params = {}) {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', name, params);
    }
  } catch (err) {
    // Swallow — analytics must never break user flow.
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[Analytics] trackEvent failed:', err && err.message ? err.message : err);
    }
  }
  // Mirror conversion events to the Meta Pixel for ad optimization.
  try {
    const metaName = META_EVENTS[name];
    if (metaName && typeof window !== 'undefined' && typeof window.fbq === 'function') {
      const metaParams = {};
      if (typeof params.value === 'number') metaParams.value = params.value;
      if (params.currency) metaParams.currency = params.currency;
      window.fbq('track', metaName, metaParams);
    }
  } catch (err) { /* never break user flow */ }
}

// Associate the GA4 session with the signed-in user for cross-session/device
// reporting + signed-in cohorts. IMPORTANT: pass the opaque Cognito `sub` ONLY —
// never email or name. Sending PII to GA violates Google's ToS.
export function setAnalyticsUser(userId) {
  if (!userId || typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  try {
    window.gtag('config', GA_MEASUREMENT_ID, { user_id: userId });
    window.gtag('set', 'user_properties', { logged_in: 'true' });
  } catch (err) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[Analytics] setAnalyticsUser failed:', err && err.message ? err.message : err);
    }
  }
}
